import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo1.png";
import defaultLogoDark from "./assets/logo2.png";
import useSettings from "./hooks/useSettings";
import "./styles/animations.css";

const defaultLogoFavicon = "/favicon.ico";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    "#065183";
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          // Custom mode property for easy access as theme.mode
          mode: mode,

          // Scrollbar styles melhorados mas usando cores do tema
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor:
                mode === "light" ? primaryColorLight : primaryColorDark,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? "#f5f5f5" : "#2a2a2a",
              borderRadius: "4px",
            },
          },

          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#E0E0E0" : "#404040",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: mode === "light" ? "#BDBDBD" : "#505050",
              }
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          },

          palette: {
            type: mode,
            mode: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark,
              light: mode === "light"
                ? `${primaryColorLight}80`
                : `${primaryColorDark}80`,
              dark: mode === "light"
                ? `${primaryColorLight}CC`
                : `${primaryColorDark}CC`,
              contrastText: "#ffffff",
            },
            text: {
              primary: mode === "light" ? "#1a1a1a" : "#e8eaed",
              secondary: mode === "light" ? "#666666" : "#9aa0a6",
            },
            background: {
              default: mode === "light" ? "#fafafa" : "#0d0f18",
              paper: mode === "light" ? "#ffffff" : "#141620",
            },
            action: {
              active: mode === "light" ? "rgba(0, 0, 0, 0.54)" : "rgba(255, 255, 255, 0.7)",
              hover: mode === "light" ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.08)",
              selected: mode === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.12)",
            },
            textPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary:
              mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#EEE" : "#1a1c2e",
            optionsBackground: mode === "light" ? "#fafafa" : "#1a1c2e",
            fancyBackground: mode === "light" ? "#fafafa" : "#0d0f18",
            total: mode === "light" ? "#fff" : "#141620",
            messageIcons: mode === "light" ? "grey" : "#8b949e",
            inputBackground: mode === "light" ? "#FFFFFF" : "#1a1c2e",
            barraSuperior: mode === "light" ? primaryColorLight : "#141620",
            divider: mode === "light" ? "#e0e0e0" : "#1e2035",
          },

          typography: {
            fontFamily: [
              'Inter',
              'Roboto',
              '-apple-system',
              'BlinkMacSystemFont',
              '"Segoe UI"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
            ].join(','),
            h1: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h2: {
              fontWeight: 700,
              letterSpacing: '-0.025em',
            },
            h3: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h4: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h5: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            h6: {
              fontWeight: 600,
              letterSpacing: '-0.025em',
            },
            button: {
              fontWeight: 600,
              textTransform: 'none',
              letterSpacing: '0.025em',
            },
          },

          shape: {
            borderRadius: 8, // Bordas arredondadas mas não excessivas
          },
          overrides: {
            // Global CssBaseline
            MuiCssBaseline: {
              '@global': {
                body: {
                  color: mode === "dark" ? "#e8eaed !important" : "#1a1a1a",
                  backgroundColor: mode === "dark" ? "#0d0f18" : "#fafafa",
                },
                ...(mode === "dark" ? {
                  // Force override ALL hardcoded dark colors in inline styles
                  'h1, h2, h3, h4, h5, h6, p, span, label, div, td, th, li, a, strong, em, b, i, small': {
                    color: '#e8eaed !important',
                  },
                  // Preserve colored elements (badges, chips, status indicators, buttons with specific colors)
                  '[style*="background"], .MuiBadge-badge, .MuiChip-root, .MuiButton-containedPrimary, .MuiButton-containedSecondary, [class*="badge"], [class*="status"]': {
                    color: 'unset !important',
                  },
                  // Re-allow white text on colored backgrounds
                  '.MuiButton-containedPrimary, .MuiButton-containedSecondary, .MuiBadge-colorPrimary, .MuiBadge-colorSecondary, .MuiBadge-colorError': {
                    color: '#fff !important',
                  },
                  // Force SVG icons white
                  'svg, svg path, .MuiSvgIcon-root, .MuiIconButton-root svg': {
                    color: 'rgba(255,255,255,0.85) !important',
                    fill: 'currentColor',
                  },
                  // Secondary text
                  '.MuiTypography-colorTextSecondary, .MuiFormHelperText-root, .MuiInputLabel-root': {
                    color: '#9aa0a6 !important',
                  },
                  // Input text
                  'input, textarea, select, .MuiInputBase-input, .MuiSelect-select': {
                    color: '#e8eaed !important',
                  },
                  // Placeholder
                  'input::placeholder, textarea::placeholder': {
                    color: 'rgba(255,255,255,0.4) !important',
                    opacity: '1 !important',
                  },
                } : {
                  'h1, h2, h3, h4, h5, h6, p, span, label, div, td, th, li, a': {
                    color: 'inherit',
                  },
                }),
              },
            },

            // Botões usando cor do tema
            MuiButton: {
              root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              },
              contained: {
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                }
              },
              outlined: {
                borderColor: mode === "dark" ? "rgba(255,255,255,0.2)" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              text: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            MuiContainer: {
              root: {
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                maxWidth: 'none !important',
                width: '100% !important',
              },
              maxWidthLg: { maxWidth: 'none !important' },
              maxWidthMd: { maxWidth: 'none !important' },
              maxWidthSm: { maxWidth: 'none !important' },
              maxWidthXl: { maxWidth: 'none !important' },
              maxWidthXs: { maxWidth: 'none !important' },
            },
          
            MuiPaper: {
              root: {
                backgroundImage: 'none',
                backgroundColor: mode === "dark" ? "#141620" : "#ffffff",
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              rounded: {
                borderRadius: 12,
              },
              elevation1: {
                boxShadow: mode === "dark" ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
              },
              elevation2: {
                boxShadow: mode === "dark" ? '0 2px 6px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
              },
              elevation3: {
                boxShadow: mode === "dark" ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
              }
            },

            MuiMenu: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: '180px !important',
                backgroundColor: mode === "dark" ? "#1a1c2e !important" : undefined,
              }
            },

            MuiPopover: {
              paper: {
                width: 'auto !important',
                maxWidth: '300px !important',
                minWidth: 'auto !important',
                backgroundColor: mode === "dark" ? "#1a1c2e" : undefined,
              }
            },

            MuiMenuItem: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            // Inputs melhorados
            MuiTextField: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? "#ccc" : "#555",
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === "light" ? primaryColorLight : primaryColorDark,
                      borderWidth: 2,
                    }
                  }
                }
              }
            },

            // Tabs
            MuiTab: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                letterSpacing: '0.025em',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.3s ease',
                color: mode === "dark" ? "rgba(255,255,255,0.7) !important" : undefined,
                '&:hover': {
                  backgroundColor: mode === "light"
                    ? `${primaryColorLight}08`
                    : `${primaryColorDark}15`,
                },
                '&.Mui-selected': {
                  color: (mode === "light" ? primaryColorLight : primaryColorDark) + " !important",
                }
              },
              textColorInherit: {
                color: mode === "dark" ? "rgba(255,255,255,0.7)" : undefined,
                opacity: 1,
              },
              textColorPrimary: {
                color: mode === "dark" ? "rgba(255,255,255,0.7)" : undefined,
              },
            },

            MuiTabs: {
              indicator: {
                backgroundColor: mode === "dark" ? primaryColorDark : primaryColorLight,
              }
            },

            // Drawer
            MuiDrawer: {
              paper: {
                border: 'none',
                backgroundColor: mode === "dark" ? "#0d0f18" : "#ffffff",
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            // AppBar
            MuiAppBar: {
              root: {
                boxShadow: mode === "dark" ? "none" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              colorDefault: {
                backgroundColor: mode === "dark" ? "#141620" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              colorPrimary: {
                color: "#ffffff",
              }
            },

            // Toolbar
            MuiToolbar: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            // Dialog
            MuiDialog: {
              paper: {
                backgroundColor: mode === "dark" ? "#141620" : "#ffffff",
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            MuiDialogTitle: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            MuiDialogContent: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            MuiDialogContentText: {
              root: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
              }
            },

            // Card
            MuiCard: {
              root: {
                backgroundColor: mode === "dark" ? "#141620" : "#ffffff",
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            MuiCardContent: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            MuiCardHeader: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              title: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              subheader: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
              },
            },

            // Icons
            MuiSvgIcon: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.85)" : undefined,
              }
            },

            MuiIconButton: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.75)" : undefined,
              }
            },

            // Input labels and text
            MuiInputLabel: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.6)" : undefined,
                '&.Mui-focused': {
                  color: mode === "dark" ? primaryColorDark : primaryColorLight,
                }
              },
              outlined: {
                color: mode === "dark" ? "rgba(255,255,255,0.6)" : undefined,
              },
              filled: {
                color: mode === "dark" ? "rgba(255,255,255,0.6)" : undefined,
              },
            },

            MuiOutlinedInput: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === "dark" ? "rgba(255,255,255,0.15)" : undefined,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === "dark" ? "rgba(255,255,255,0.3)" : undefined,
                },
              },
              input: {
                color: mode === "dark" ? "#e8eaed" : undefined,
                '&::placeholder': {
                  color: mode === "dark" ? "rgba(255,255,255,0.4)" : undefined,
                  opacity: 1,
                },
              },
            },

            MuiFilledInput: {
              root: {
                backgroundColor: mode === "dark" ? "rgba(255,255,255,0.05)" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
                '&:hover': {
                  backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : undefined,
                },
                '&.Mui-focused': {
                  backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : undefined,
                },
              },
              input: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            MuiInput: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              underline: {
                '&:before': {
                  borderBottomColor: mode === "dark" ? "rgba(255,255,255,0.2)" : undefined,
                },
              },
            },

            MuiInputBase: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              input: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            MuiFormHelperText: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.5)" : undefined,
              },
            },

            MuiFormLabel: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.6)" : undefined,
                '&.Mui-focused': {
                  color: mode === "dark" ? primaryColorDark : undefined,
                },
              },
            },

            MuiSelect: {
              icon: {
                color: mode === "dark" ? "rgba(255,255,255,0.7)" : undefined,
              },
              select: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            // Table
            MuiTableCell: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
                borderBottom: mode === "dark" ? "1px solid rgba(255,255,255,0.06)" : undefined,
              },
              head: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
                backgroundColor: mode === "dark" ? "#1a1c2e" : undefined,
                fontWeight: 600,
              },
              body: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            MuiTableRow: {
              root: {
                '&:hover': {
                  backgroundColor: mode === "dark" ? "rgba(255,255,255,0.03) !important" : undefined,
                },
              },
            },

            MuiTableHead: {
              root: {
                backgroundColor: mode === "dark" ? "#1a1c2e" : undefined,
              },
            },

            MuiTablePagination: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              caption: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
              },
              selectIcon: {
                color: mode === "dark" ? "rgba(255,255,255,0.7)" : undefined,
              },
            },

            MuiChip: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
                borderColor: mode === "dark" ? "rgba(255,255,255,0.15)" : undefined,
              },
              outlined: {
                borderColor: mode === "dark" ? "rgba(255,255,255,0.2)" : undefined,
              },
              deleteIcon: {
                color: mode === "dark" ? "rgba(255,255,255,0.5)" : undefined,
              },
            },

            MuiTypography: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              colorTextSecondary: {
                color: mode === "dark" ? "#9aa0a6 !important" : undefined,
              },
              colorTextPrimary: {
                color: mode === "dark" ? "#e8eaed !important" : undefined,
              },
              colorInherit: {
                color: "inherit",
              },
            },

            MuiSwitch: {
              root: {
                width: 42,
                height: 24,
                padding: 0,
                display: "flex",
              },
              switchBase: {
                padding: 3,
                color: mode === "dark" ? "#718096" : "#a0aec0",
                "&$checked": {
                  transform: "translateX(18px)",
                  color: "#fff",
                  "& + $track": {
                    opacity: 1,
                    backgroundColor: "#555",
                    borderColor: "#777",
                  },
                },
                "&$checked + $track": {
                  opacity: 1,
                  backgroundColor: "#555",
                  borderColor: "#777",
                },
              },
              thumb: {
                width: 18,
                height: 18,
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              },
              track: {
                borderRadius: 12,
                opacity: 1,
                backgroundColor: mode === "dark" ? "#2d3748" : "#cbd5e0",
                border: mode === "dark" ? "1px solid #4a5568" : "1px solid #a0aec0",
              },
              checked: {},
            },

            MuiFormControlLabel: {
              label: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              }
            },

            // Divider
            MuiDivider: {
              root: {
                backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : undefined,
              },
            },

            // Accordion
            MuiAccordion: {
              root: {
                backgroundColor: mode === "dark" ? "#141620" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            MuiAccordionSummary: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            MuiAccordionDetails: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            // Tooltip
            MuiTooltip: {
              tooltip: {
                backgroundColor: mode === "dark" ? "#2a2d40" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            // Badge
            MuiBadge: {
              colorSecondary: {
                color: "#ffffff",
              },
            },

            // Breadcrumbs
            MuiBreadcrumbs: {
              root: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
              },
            },

            // Stepper
            MuiStepLabel: {
              label: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
                '&.MuiStepLabel-active': {
                  color: mode === "dark" ? "#e8eaed" : undefined,
                },
                '&.MuiStepLabel-completed': {
                  color: mode === "dark" ? "#e8eaed" : undefined,
                },
              },
            },

            // ListItem active styling
            MuiListItem: {
              root: {
                color: mode === "dark" ? "#e8eaed" : undefined,
                '&.Mui-selected': {
                  backgroundColor: mode === "dark"
                    ? `${primaryColorDark}15`
                    : `${primaryColorLight}10`,
                  '&:hover': {
                    backgroundColor: mode === "dark"
                      ? `${primaryColorDark}20`
                      : `${primaryColorLight}15`,
                  },
                  '& .MuiListItemIcon-root': {
                    color: mode === "dark" ? primaryColorDark : primaryColorLight,
                  },
                  '& .MuiListItemText-primary': {
                    color: mode === "dark" ? primaryColorDark : primaryColorLight,
                    fontWeight: 600,
                  },
                }
              }
            },

            MuiListItemIcon: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.7)" : undefined,
              }
            },

            MuiListItemText: {
              primary: {
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
              secondary: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
              },
            },

            MuiListSubheader: {
              root: {
                color: mode === "dark" ? "#9aa0a6" : undefined,
                backgroundColor: mode === "dark" ? "#141620" : undefined,
              },
            },

            // Checkbox / Radio
            MuiCheckbox: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.5)" : undefined,
              },
            },

            MuiRadio: {
              root: {
                color: mode === "dark" ? "rgba(255,255,255,0.5)" : undefined,
              },
            },

            // Snackbar
            MuiSnackbarContent: {
              root: {
                backgroundColor: mode === "dark" ? "#2a2d40" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },

            // Backdrop
            MuiBackdrop: {
              root: {
                backgroundColor: mode === "dark" ? "rgba(0,0,0,0.7)" : undefined,
              },
            },

            // Avatar fallback text
            MuiAvatar: {
              colorDefault: {
                backgroundColor: mode === "dark" ? "#2a2d40" : undefined,
                color: mode === "dark" ? "#e8eaed" : undefined,
              },
            },
          },

          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight, // Essas são as cores que vêm do tema dinâmico
    ]
  );

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#0000FF");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#39ACE7");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        const lightLogoUrl = file ? getBackendUrl() + "/public/" + file : defaultLogoLight;
        setAppLogoLight(lightLogoUrl);
        localStorage.setItem("wl_logoLight", lightLogoUrl);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        const darkLogoUrl = file ? getBackendUrl() + "/public/" + file : defaultLogoDark;
        setAppLogoDark(darkLogoUrl);
        localStorage.setItem("wl_logoDark", darkLogoUrl);
        // A splash usa appLogoSplash quando disponível; o fallback por tema é tratado abaixo.
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoSplash")
      .then((file) => {
        if (!file) return;
        const splashUrl = getBackendUrl() + "/public/" + file;
        localStorage.setItem("wl_logoSplash", splashUrl);
        const splashLogo = document.getElementById('splash-logo');
        if (splashLogo) splashLogo.src = splashUrl;
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        const faviconUrl = file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon;
        setAppLogoFavicon(faviconUrl);
        localStorage.setItem("wl_favicon", faviconUrl);

        // Atualiza o manifest.json com favicon, appLogoMobile e appNameMobile
        Promise.all([
          getPublicSetting("appNameMobile"),
          getPublicSetting("appLogoMobile"),
        ])
          .then(([mobileName, mobileLogoFile]) => {
            const manifestLink = document.querySelector('link[rel="manifest"]');
            if (manifestLink) {
              // Sempre buscar o manifest original estático, nunca o blob URL
              const originalManifestUrl = window.location.origin + "/manifest.json";
              fetch(originalManifestUrl)
                .then(r => r.json())
                .then(manifest => {
                  if (mobileName) {
                    manifest.name = mobileName;
                    manifest.short_name = mobileName;
                  }
                  // Prioriza appLogoMobile para ícones do PWA, senão usa favicon
                  const iconUrl = mobileLogoFile
                    ? getBackendUrl() + "/public/" + mobileLogoFile
                    : (file ? faviconUrl : null);
                  if (iconUrl) {
                    // Cache-buster para forçar o navegador / PWA a recarregar o ícone
                    const versionedIcon = iconUrl + (iconUrl.includes("?") ? "&" : "?") + "v=" + Date.now();
                    manifest.icons = [
                      { src: versionedIcon, sizes: "96x96",   type: "image/png", purpose: "any" },
                      { src: versionedIcon, sizes: "144x144", type: "image/png", purpose: "any" },
                      { src: versionedIcon, sizes: "192x192", type: "image/png", purpose: "any" },
                      { src: versionedIcon, sizes: "512x512", type: "image/png", purpose: "any" },
                      { src: versionedIcon, sizes: "192x192", type: "image/png", purpose: "maskable" },
                      { src: versionedIcon, sizes: "512x512", type: "image/png", purpose: "maskable" }
                    ];

                    // Atualizar apple-touch-icon (iOS PWA não usa manifest.icons)
                    let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
                    if (!appleIcon) {
                      appleIcon = document.createElement("link");
                      appleIcon.rel = "apple-touch-icon";
                      document.head.appendChild(appleIcon);
                    }
                    appleIcon.href = versionedIcon;

                    // Atualizar favicon do browser também (rel="icon")
                    const iconLink = document.querySelector('link[rel="icon"]');
                    if (iconLink) iconLink.href = versionedIcon;
                  }
                  // Revogar blob URL anterior se existir
                  if (manifestLink.href.startsWith("blob:")) {
                    URL.revokeObjectURL(manifestLink.href);
                  }
                  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
                  manifestLink.href = URL.createObjectURL(blob);
                })
                .catch(() => {});
            }
          })
          .catch(() => {});
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        const finalName = name || "Equipechat";
        setAppName(finalName);
        localStorage.setItem("wl_appName", finalName);
        document.title = finalName;
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName("Equipechat");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
